import { colors } from "healthex-common/colors";
import { fontSizes } from "healthex-common/sizes";
import { fontWeights } from "healthex-common/weights";
import styled, { keyframes } from "styled-components";
import { multilineEllipsis } from "./base.styles";
import { FlexColumn, FlexRow } from "./common.styles";
export const TimelinePageRoot = styled.div `
    display: flex;
    flex-direction: column;
    min-height: 100%;
    flex: 1;
    width: 100%;
    background-color: ${colors.history_timeline_card_bg};
`;
export const HeroSection = styled.div `
    position: relative;
    width: 100%;
    padding: 32px 24px;
    border-bottom: 0.37px solid ${colors.history_separator};
    box-sizing: border-box;
    overflow: hidden;
    isolation: isolate;
    background-color: ${colors.history_hero_bg};

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: linear-gradient(41.44deg, rgba(153, 157, 162, 0.2) 0%, rgba(186, 188, 191, 0) 100%);
        pointer-events: none;
        z-index: 0;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background-image: radial-gradient(ellipse 70% 140% at 12% 40%, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 100%);
        pointer-events: none;
        z-index: 0;
    }

    @media print {
        &::before,
        &::after {
            display: none;
        }
    }
`;
export const HeroInner = styled.div `
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
`;
export const HeroTextColumn = styled.div `
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 0px;
`;
export const HeroButtonColumn = styled.div `
    flex-shrink: 0;
`;
export const HeadlinePrimary = styled.h1 `
    margin: 0;
    font-size: 30px;
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_main};
    line-height: 1.2;
    letter-spacing: 0.12px;
    width: 100%;
    ${multilineEllipsis(2)}
`;
export const HeadlineSecondary = styled.p `
    margin: 0;
    font-size: 30px;
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_lighter};
    line-height: 1.2;
    letter-spacing: 0.12px;
    width: 100%;
    ${multilineEllipsis(2)}
`;
export const HeroDescription = styled.p `
    margin: 0;
    padding-top: 16px;
    font-size: ${fontSizes.STANDARD};
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.black_lighter};
    line-height: 24px;
    letter-spacing: -0.31px;
    width: 100%;
`;
export const MoreButton = styled.button `
    width: 32px;
    height: 32px;
    border-radius: 100px;
    border: ${(props) => (props.$noBorder ? "none" : `1px solid ${colors.black_lighter}`)};
    color: ${colors.black_lighter};
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    padding: 0;

    @media (hover: hover) {
        &:hover {
            background-color: ${colors.black_4};
        }
    }

    @media print {
        display: none !important;
    }
`;
export const EmptyStateSection = styled(FlexColumn) `
    align-items: center;
    justify-content: center;
    padding: 24px 97px;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
`;
export const EmptyStateImage = styled.img `
    display: block;
    width: auto;
    height: 80px;
    max-width: 100%;
    flex-shrink: 0;
`;
export const EmptyStateText = styled.p `
    margin: 0;
    font-size: ${fontSizes.STANDARD};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_lighter};
    text-align: center;
    line-height: 21px;
    letter-spacing: -0.31px;
    min-width: 100%;
`;
export const NoMatchesSection = styled(FlexColumn) `
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    padding: 32px 24px 48px;
`;
export const NoMatchesText = styled.p `
    margin: 0;
    max-width: 320px;
    font-size: ${fontSizes.STANDARD};
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.black_lighter};
    text-align: center;
    line-height: 24px;
    letter-spacing: -0.31px;
`;
export const FindMyRecordsButton = styled.button `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 44px;
    padding: 0 24px;
    background-color: ${colors.navy_light};
    border: none;
    border-radius: 100px;
    cursor: pointer;
    flex-shrink: 0;

    @media (hover: hover) {
        &:hover {
            background-color: ${colors.navy_main};
        }
    }
`;
export const FindMyRecordsButtonLabel = styled.span `
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.history_timeline_card_bg};
    white-space: nowrap;
`;
export const FilterPillsRow = styled.div `
    display: flex;
    align-items: center;
    gap: 8px;
    padding: ${(props) => (props.$sourcesTabDisabled ? "16px" : "8px")} 16px 16px 16px;
    overflow-x: auto;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;

    &::-webkit-scrollbar {
        display: none;
    }
`;
export const FilterPillActive = styled.button `
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 16px;
    background-color: ${colors.navy_main};
    border: none;
    border-radius: 100px;
    flex-shrink: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.white_main};
    white-space: nowrap;
    letter-spacing: -0.08px;
    line-height: 20px;
    cursor: pointer;
`;
export const FilterPillInactive = styled.button `
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 16px;
    background-color: ${colors.navy_8};
    border: none;
    border-radius: 100px;
    flex-shrink: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${fontWeights.NORMAL};
    color: ${(props) => (props.$disabled ? colors.navy_30 : colors.navy_light)};
    white-space: nowrap;
    letter-spacing: -0.08px;
    line-height: 20px;
    cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
`;
export const FilterIconPill = styled.button `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${(props) => (props.$active ? "4px" : "0")};
    width: ${(props) => (props.$active ? "auto" : "28px")};
    height: 28px;
    padding: ${(props) => (props.$active ? "0 12px 0 10px" : "0")};
    background-color: ${(props) => (props.$active ? colors.navy_main : colors.navy_8)};
    border: none;
    border-radius: 100px;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${fontWeights.NORMAL};
    color: ${(props) => (props.$active ? colors.white_main : colors.navy_light)};
    white-space: nowrap;
    letter-spacing: -0.08px;
    line-height: 20px;
    flex-shrink: 0;
    cursor: pointer;
`;
export const YearStrip = styled.div `
    display: flex;
    align-items: center;
    padding: 8px 16px;
    background-color: ${colors.white_main};
    border-top: 0.57px solid ${colors.history_separator};
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
`;
export const YearLabel = styled.p `
    margin: 0;
    font-size: ${fontSizes.STANDARD};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_lighter};
    letter-spacing: -0.31px;
    line-height: 21px;
`;
export const TimelineCardRoot = styled.div `
    background-color: ${colors.history_timeline_card_bg};
    width: 100%;
    flex-shrink: 0;
`;
export const TimelineCardInner = styled.button `
    display: flex;
    align-items: center;
    width: 100%;
    background: transparent;
    border: none;
    border-top: 0.5px solid ${colors.history_separator};
    padding: 8px 16px;
    box-sizing: border-box;
    cursor: pointer;
    text-align: left;

    @media (hover: hover) {
        &:hover {
            background-color: ${colors.black_4};
        }
    }
`;
export const TimelineCardContent = styled.div `
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 8px;
    gap: 6px;
    min-width: 0;
`;
export const TimelineCardTitleRow = styled.div `
    font-size: ${fontSizes.STANDARD};
    letter-spacing: -0.31px;
    width: 100%;
    min-width: 0;
    // Character-exact single-line truncation (cuts mid-word, e.g. "Tra...") rather than
    // multilineEllipsis's word-wrap-then-clamp behavior, which would hide a whole trailing
    // word instead of cutting it off partway through.
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    & > * + * {
        margin-left: 2px;
    }
`;
export const TimelineCardSubtype = styled.span `
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_main};
    line-height: 21px;
    vertical-align: baseline;
`;
export const TimelineCardSubtypeSep = styled.span `
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.history_secondary_text};
    line-height: 24px;
    vertical-align: baseline;
`;
export const TimelineCardTitle = styled.span `
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.history_secondary_text};
    line-height: 24px;
    vertical-align: baseline;
`;
export const TimelineCardMetaRow = styled.div `
    font-size: ${fontSizes.TINY};
    color: ${colors.history_secondary_text};
    line-height: 1;
    width: 100%;
    min-width: 0;
    ${multilineEllipsis(1)}

    /* Preserve the previous 4px flex gap between children. */
    & > * + * {
        margin-left: 4px;
    }
`;
export const TimelineCardMetaBold = styled.span `
    font-weight: ${fontWeights.BOLD};
`;
export const TimelineCardMetaRegular = styled.span `
    font-weight: ${fontWeights.NORMAL};
    letter-spacing: 0.4px;
`;
export const TimelineCardChevronWrapper = styled.div `
    display: flex;
    align-items: center;
    align-self: stretch;
    flex-shrink: 0;
    color: ${colors.black_lighter};
`;
export const GapCardRoot = styled.div `
    background-color: ${colors.history_timeline_card_bg};
    width: 100%;
    flex-shrink: 0;
`;
export const GapCardHeader = styled.div `
    display: flex;
    align-items: flex-start;
    border-top: 0.57px solid ${colors.history_separator};
    padding: 16px 16px 16px 24px;
    box-sizing: border-box;
    width: 100%;
`;
export const GapCardTextBlock = styled.div `
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;
export const GapCardTitle = styled.p `
    margin: 0;
    font-size: 19px;
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_main};
    line-height: 28px;
    letter-spacing: -0.45px;
    text-align: left;
`;
export const GapCardDateRange = styled.p `
    margin: 0;
    font-size: ${fontSizes.TINY};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.history_secondary_text};
    line-height: 18px;
    white-space: nowrap;
`;
export const GapCardDescription = styled.p `
    margin: 0;
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.history_secondary_text};
    line-height: 21.5px;
    letter-spacing: -0.15px;
    text-align: left;
`;
export const GapCardActions = styled.div `
    display: flex;
    align-items: center;
    padding: 8px 16px 16px;
    box-sizing: border-box;
    width: 100%;
`;
export const GapCardButtonRow = styled(FlexRow) `
    flex: 1;
    gap: 8px;
    align-items: center;
`;
export const GapPrimaryActionButton = styled.button `
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 44px;
    padding: 0 24px;
    background-color: ${colors.navy_light};
    border: none;
    border-radius: 100px;
    cursor: pointer;

    @media (hover: hover) {
        &:hover {
            background-color: ${colors.navy_main};
        }
    }
`;
export const GapPrimaryActionButtonLabel = styled.span `
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.history_timeline_card_bg};
    white-space: nowrap;
`;
export const DismissButton = styled.button `
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 44px;
    padding: 0 24px;
    background-color: ${colors.white_main};
    border: 1.37px solid ${colors.navy_light};
    border-radius: 100px;
    cursor: pointer;

    @media (hover: hover) {
        &:hover {
            background-color: ${colors.navy_4};
        }
    }
`;
export const DismissButtonLabel = styled.span `
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.navy_light};
    white-space: nowrap;
`;
const shimmer = keyframes `
    0% {
        background-position: -200% center;
    }
    100% {
        background-position: 200% center;
    }
`;
export const LoadingMoreFooter = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
    width: 100%;
    flex-shrink: 0;

    @media print {
        display: none !important;
    }
`;
export const LoadingMoreText = styled.span `
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.BOLD};
    line-height: 21.5px;
    letter-spacing: -0.15px;
    text-align: center;
    background-image: linear-gradient(90deg, ${colors.black_lighter} 0%, #bdcbd6 35%, ${colors.black_lighter} 52.325%, #bdcbd6 98.19%);
    background-size: 200% auto;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    animation: ${shimmer} 2.5s linear infinite;
`;
export const InitialLoadingSection = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
`;
export const InfiniteScrollSentinel = styled.div `
    width: 100%;
    height: 1px;
    min-height: 1px;
    flex-shrink: 0;

    @media print {
        display: none !important;
    }
`;
export const HomeContainer = styled(FlexColumn) `
    gap: 40px;
    padding: 32px 24px;
    align-items: center;
`;
export const HomeSummaryContainer = styled(FlexColumn) `
    width: 100%;
`;
export const HomeCallToActionContainer = styled(FlexColumn) `
    gap: 12px;
    align-self: stretch;
`;
